import { z } from 'zod';
import { searchApiPagingFromHttp$ } from 'v5/api/_common/query/pagination.http.schema';
import { rowLevelValidation } from 'v5/platform/row-level-validation';

/**
 * Safely convert date values (string or number) to Date objects for preprocessing
 * For required date fields, throws an error if null/undefined
 */
function safeDatePreprocess(val: unknown): Date | null | undefined {
  if (val == null) return val as null | undefined;

  if (typeof val === 'string') {
    return new Date(val);
  } else if (typeof val === 'number') {
    // Assume Unix timestamp in seconds
    return new Date(val * 1000);
  }

  return undefined;
}

/**
 * Preprocess function for required date fields - throws if null/undefined
 */
function requiredDatePreprocess(val: unknown): Date {
  if (val == null) {
    throw new Error(`Required date field cannot be null or undefined: ${val}`);
  }

  if (typeof val === 'string') {
    return new Date(val);
  } else if (typeof val === 'number') {
    // Assume Unix timestamp in seconds
    return new Date(val * 1000);
  }

  throw new Error(`Invalid date value: ${val}`);
}

// Basic resource/contact schema for API responses (strict)
const resource$ = z.looseObject({
  id: z.number(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  profile_picture: z.string().nullable().optional(),
  resource_type_id: z.number().nullable().optional(),
  pronouns_option_id: z.number().nullable().optional()
});

// Booking type schema
const bookingType$ = z.object({
  id: z.number(),
  name: z.string(),
  model: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  order: z.number().nullable().optional(),
  archived: z.boolean().nullable().optional(),
  include_weekends_default: z.boolean().nullable().optional(),
  default_all_day_hours: z.number().nullable().optional(),
  metastructure: z.record(z.string(), z.any()).nullable().optional(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(safeDatePreprocess, z.date()).nullable().optional(),
  updated_at: z.preprocess(safeDatePreprocess, z.date()).nullable().optional(),
  created_mechanism: z.string().nullable()
});

// Main booking schema matching API response structure (unknown fields stripped)
export const booking$ = z.looseObject({
  // Core booking fields
  id: z.number(),
  guid: z.string(),
  user_id: z.string().nullable(),
  archived: z.boolean(),
  status_id: z.number().nullable().optional(),
  contact_id: z.number().nullable().optional(),
  booking_type_id: z.number(),
  created_at: z.preprocess(requiredDatePreprocess, z.date()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable().optional()),

  // Status and dates
  status_type: z.string().optional(),
  booking_date: z.preprocess(
    safeDatePreprocess,
    z.date().nullable().optional()
  ),
  booking_start_date: z.preprocess(
    safeDatePreprocess,
    z.date().nullable().optional()
  ),
  booking_end_date: z.preprocess(
    safeDatePreprocess,
    z.date().nullable().optional()
  ),
  booking_date_range: z.array(z.string()).optional(),
  cancelled_at: z.preprocess(
    safeDatePreprocess,
    z.date().nullable().optional()
  ),
  confirmed_at: z.preprocess(
    safeDatePreprocess,
    z.date().nullable().optional()
  ),
  pencilled_at: z.preprocess(
    safeDatePreprocess,
    z.date().nullable().optional()
  ),
  confirmation_sent_at: z.preprocess(
    safeDatePreprocess,
    z.date().nullable().optional()
  ),

  // Contact info
  contact_name: z.string().optional(),

  // Rate information
  rate_amount: z.number().nullable().optional(),
  rate_currency: z.string().optional().nullable(),
  rate_agency_fee: z.number().nullable().optional(),
  rate_payroll_fee: z.number().nullable().optional(),
  rate_processing_fee: z.number().nullable().optional(),
  rate_interval: z.string().optional().nullable(),

  // Additional fields
  licence: z.string().optional().nullable(),
  note: z.string().optional().nullable(),
  submitted_date: z.preprocess(
    safeDatePreprocess,
    z.date().nullable().optional()
  ),
  agreed_date: z.preprocess(safeDatePreprocess, z.date().nullable().optional()),

  // Relations and custom fields (as they come from API)
  contracts: z.array(z.any()).optional().default([]),
  invoices: z.array(z.any()).optional().default([]),
  events: z.array(z.any()).optional().default([]),
  custom_fields: z.array(z.any()).optional().default([]),
  resource_slot_assignments: z.array(z.any()).optional().default([]),
  contact: resource$.nullable().optional(),
  contact_rate_id: z.number().nullable().optional(),
  rate_id: z.number().nullable().optional(),
  booking_type: bookingType$,
  created_mechanism: z.string().nullable()
});

/** Parsed booking JSON from list/detail endpoints (before domain conversion). */
export type BookingFromHttp = z.infer<typeof booking$>;

/**
 * Full `/search/bookings` JSON: envelope + paging, then per-row wire validation (same pattern as
 * the `/search/contacts` list schema in `resource.api.http.schema.ts`). Shape `{ items, count }`
 * and domain mapping are built in {@link createBookingApi} after {@link parseWithDecoratedError}.
 */
export const bookingSearchListResponse$ = z.object({
  results: rowLevelValidation.withRowLevelValidation(booking$),
  paging: searchApiPagingFromHttp$
});

// Payload for creating bookings (loose for partial transform)
export const createBookingSchema = z.looseObject({
  contact_id: z.number(),
  booking_type_id: z.number(),
  rate_id: z.number().optional(),
  rate_amount: z.number().optional(),
  rate_currency: z.string().optional(),
  rate_interval: z.enum(['DAILY', 'HOURLY', 'FIXED']).optional(),
  note: z.string().optional(),
  licence: z.string().optional(),
  estimated_daily_hours: z.number().optional(),
  include_weekends: z.boolean().optional()
});

// Payload for updating bookings (loose for partial transform)
export const updateBookingSchema = z
  .looseObject({
    contact_id: z.number().optional(),
    status_id: z.number().optional(),
    rate_id: z.number().optional(),
    rate_amount: z.number().optional(),
    rate_currency: z.string().optional(),
    rate_interval: z.enum(['DAILY', 'HOURLY', 'FIXED']).optional(),
    note: z.string().optional(),
    licence: z.string().optional(),
    estimated_daily_hours: z.number().optional(),
    archived: z.boolean().optional()
  })
  .partial();

/** Transform amount from whole units to cents when present (for API request). */
const amountToCents$ = z
  .number()
  .optional()
  .transform(n => (typeof n === 'number' ? Math.round(n * 100) : undefined));

const documentDataToApi$ = z
  .looseObject({
    amount: amountToCents$,
    currency: z.string().optional(),
    reference: z.string().optional(),
    status: z.string().optional()
  })
  .optional();

const filePayloadToApi$ = z.union([
  z.looseObject({ id: z.number() }),
  z.looseObject({
    name: z.string(),
    key: z.string(),
    bucket: z.string(),
    mime_type: z.string(),
    size: z.number(),
    provider: z.enum(['azure', 's3'])
  })
]);

/** Single invoice item for create (POST invoices). Payload – loose for partial transform. */
export const createInvoiceItemToApi$ = z.looseObject({
  document_data: documentDataToApi$,
  file: filePayloadToApi$.optional(),
  status: z.string().optional(),
  url: z.string().optional()
});

/** Payload for update invoice (PUT invoice). Loose for partial transform. */
export const updateInvoicePayloadToApi$ = z.looseObject({
  document_data: documentDataToApi$,
  status: z.string().optional(),
  file: filePayloadToApi$.optional()
});

/** Single receipt item for create (POST receipts). Payload – loose for partial transform. */
export const createReceiptItemToApi$ = z.looseObject({
  document_data: documentDataToApi$,
  file: filePayloadToApi$.optional(),
  status: z.string().optional()
});

/** Payload for update receipt (PUT receipt). Loose for partial transform. */
export const updateReceiptPayloadToApi$ = z.looseObject({
  document_data: documentDataToApi$,
  status: z.string().optional(),
  file: filePayloadToApi$.optional()
});

/** Document (invoice/receipt) response from create/update endpoints (strict). */
const documentFileResponse$ = z.object({
  id: z.number(),
  name: z.string().optional().nullable(),
  key: z.string().optional().nullable(),
  bucket: z.string().optional().nullable(),
  mime_type: z.string().optional().nullable(),
  size: z.number().optional().nullable(),
  provider: z.string().optional().nullable(),
  url: z.string().optional().nullable()
});

export const documentResponse$ = z.object({
  id: z.number(),
  entity_type: z.number(),
  entity_id: z.number(),
  document_type: z.string().nullable().optional(),
  file_id: z.number().nullable().optional(),
  status: z.union([z.number(), z.string()]).optional().nullable(),
  document_data: z
    .object({
      reference: z.string().optional().nullable(),
      amount: z.number().optional().nullable(),
      currency: z.string().optional().nullable(),
      status: z.string().optional().nullable()
    })
    .nullable()
    .optional(),
  url: z.string().nullable().optional(),
  order: z.number().nullable().optional(),
  provider: z.string().nullable().optional(),
  archived: z.boolean().optional(),
  user_id: z.string().nullable().optional(),
  created_mechanism: z.string().nullable().optional(),
  created_at: z.string().optional().nullable(),
  updated_at: z.string().optional().nullable(),
  file: documentFileResponse$.nullable().optional(),
  tags: z.array(z.unknown()).optional()
});

export type DocumentResponse = z.infer<typeof documentResponse$>;
